import { dehydrate, QueryClient } from "@tanstack/react-query";

interface SSRQueryFetcher<TData> {
  key: readonly unknown[];
  fn: () => Promise<TData>;
}

interface WithSSRQueryParams {
  fetchers: SSRQueryFetcher<unknown>[];
}

interface WithSSRQueryResult {
  queryClient: QueryClient;
  dehydratedState: ReturnType<typeof dehydrate>;
  getInitialData<T>(key: readonly unknown[]): T | undefined;
}

export const getQueryClient = () => new QueryClient();
export { dehydrate };

export const withSSRQuery = async ({
  fetchers,
}: WithSSRQueryParams): Promise<WithSSRQueryResult> => {
  const queryClient = getQueryClient();

  await Promise.all(
    fetchers.map(({ key, fn }) =>
      queryClient.prefetchQuery({ queryKey: key, queryFn: fn }),
    ),
  );

  const dehydratedState = dehydrate(queryClient);

  return {
    queryClient,
    dehydratedState,
    getInitialData: <T>(key: readonly unknown[]) =>
      queryClient.getQueryData<T>(key),
  };
};
